import { ApiException } from "./API";

export const ETag = Symbol("Response ETag");

export default class ClientBase {
   /** Extra headers to add to all requests */
   public extraHeaders?: Record<string, string>;

   public onError: (message: string, httpStatus: number) => void = () => void 0;

   protected async transformResult<T>(url: string, response: Response, processor: (response: Response) => Promise<T>) {
      try {
         const result = await processor(response);

         const etag = response.headers.get("etag");
         if (etag) {
            Object.defineProperty(result, ETag, {
               enumerable: false,
               value: etag
            });
         }
         return result;
      }
      catch (ex) {
         // Processor will throw exception for non-successful status codes:
         // 1. of type defined by ProducesResponseTypeAttribute 
         //    (ex.: [ProducesResponseType<MyCustomException>(StatusCodes.Status418ImATeapot)] will throw MyCustomException for HTTP Status 418)
         // 2. ProblemDetails when only status code is defined by ProducesResponseTypeAttribute. 
         //    A machine-readable format for specifying errors in HTTP API responses based on <see href="https://tools.ietf.org/html/rfc7807"/>.
         // 3. ApiException when nothing was defined but status code is unsuccessfuff (not 200, not 204)
         //    It is also possible to have not parsed ProblemDetails

         // IMPORTANT!
         // ProblemDetails for >=400 status codes generated automatically for controllers marked with [ApiControllerAttribute]. 
         // https://learn.microsoft.com/en-us/aspnet/core/web-api/?view=aspnetcore-9.0#problem-details-for-error-status-codes

         // ApiException has its own default handler
         if (ApiException.isApiException(ex)) {
            this.handleApiException(ex, url);
         }
         // else {
         // exception intentionally thrown by processor
         // ex: ResponseWrapper, ProblemDetails
         // }

         throw ex;
      }
   }

   protected handleApiException(ex: ApiException, url: string) {
      if (ex.status === 401) {
         let redirectURL = `/account/logon?returnUrl=${encodeURIComponent(window.location.pathname)}`;
         let errorMessage = "UNAUTHORIZED. You'll be redirected to login page";

         const responseContentType = ex.headers["content-type"]?.split(";")[0];
         if (["application/json", "application/problem+json"].indexOf(responseContentType) != -1) {
            try {
               const data = JSON.parse(ex.response);
               if (data && data["ls.challengeReason"] > 0) {
                  if (data.detail) {
                     errorMessage = "UNAUTHORIZED. " + data.detail;
                  }
                  if (data["ls.challengeReason"] == 4) {
                     //blocked
                     redirectURL = `/?blockedUserMessageType=${LSCommon.Models.BlockedUserMessageType.Blocked}`;
                  }
                  else if (data["ls.challengeReason"] == 5) {
                     //locked
                     redirectURL = `/?blockedUserMessageType=${LSCommon.Models.BlockedUserMessageType.Locked}`;
                  }
               }
            }
            catch {/* */ }
         }

         this.onError(errorMessage, ex.status);
         window.location.href = redirectURL;
      } else if (ex.status === 403) {
         this.onError(`FORBIDDEN. You don't have permission to access ${new URL(url).pathname}`, ex.status);
      } else if (ex.status >= 500 && ex.status <= 600) {
         this.onError(ex.response, ex.status);
      }
   }

   protected transformOptions(options: RequestInit): Promise<RequestInit> {
      options.credentials = "include";
      options.headers = {
         ...options.headers,
         ...this.extraHeaders,
      };
      return new Promise((resolve) => {
         resolve(options);
      });
   }
}
